import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { RestLink } from 'apollo-link-rest';
import { setContext } from 'apollo-link-context';
// config
import { anomsApi, mapLayerApi } from 'src/config';
// utils
import { getDeployedProductId, alert } from 'src/utils';
import { signOut } from 'src/app/functions/core';
import { getAuth, removeAuth } from 'src/app/functions/storage';
// auth (Part D: MSAL token for 401 and auth link)
import { logout as authLogout, clearAccessTokenCache } from 'src/auth/authProvider';
// introspection Query
import introspectionQueryResultData from 'src/fragmentTypes.json';

// for config please look @ https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
const defaultOptions: any = {
  watchQuery: {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },
  mutate: {
    errorPolicy: 'all',
  },
};

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const cache = new InMemoryCache({
  fragmentMatcher,
});
const getUri = (): string => `${anomsApi}${getDeployedProductId()}`;

let refetch = false;

type CreateClientOptions =
  | { getToken: () => Promise<string> }
  | { authorization: string; refreshToken: string | null; refreshTokenExpiry: number };

const isGetTokenOptions = (
  opts: CreateClientOptions
): opts is { getToken: () => Promise<string> } =>
  typeof (opts as { getToken?: () => Promise<string> }).getToken === 'function';

export const createClient = (options: CreateClientOptions): any => {
  const useMsal = isGetTokenOptions(options);
  const getToken = useMsal ? options.getToken : (): Promise<string> => {
    const auth = getAuth();
    const authHeader = auth?.authorization;
    if (!authHeader) { return Promise.reject(new Error('No auth')); }
    return Promise.resolve(authHeader.replace(/^Bearer\s+/i, ''));
  };

  let refreshingPromise: Promise<string> | undefined;

  const customFetch: any = (uri: string, opts: any) => fetch(uri, opts).then((response: any) => {
    if (response && response.status === 401) {
      if (!refreshingPromise) {
        clearAccessTokenCache();
        refreshingPromise = getToken()
          .then((token) => {
            refreshingPromise = undefined;
            return `Bearer ${token}`;
          })
          .catch(() => {
            refreshingPromise = undefined;
            console.error('Error 401: token refresh failed');
            authLogout();
            signOut('401');
            return null;
          });
      }
      return refreshingPromise.then((authorization: string | null) => {
        if (!authorization) { return response; }
        const newOpts = { ...opts };
        newOpts.headers = { ...newOpts.headers, Authorization: authorization };
        return fetch(uri, newOpts);
      });
    }
    if (response && response.status === 400) {
      alert('500');
    } else if (response && response.status === 504) {
      alert('504');
    } else if (response && response.status === 403) {
      console.error('Error 403.001', 'signOut');
      authLogout();
    } else if (response && response.status === 404) {
      console.error('Error 404.001', 'signOut');
      authLogout();
    } else if (response && response.status === 500) {
      console.error('Error 500.001');
      const { body } = opts;
      if (typeof body !== 'undefined' && body && !refetch) {
        console.info('-> refetching the required data...');
        refetch = true;
        return fetch(uri, opts);
      }
      alert('500');
    } else if (response && response.status === 200 && refetch) {
      refetch = false;
    }
    return response;
  });

  const authLink = setContext(async (_, { headers }) => {
    if (useMsal) {
      try {
        const token = await getToken();
        return { headers: { ...headers, Authorization: `Bearer ${token}` } };
      } catch {
        removeAuth();
        authLogout();
        signOut('401');
        return { headers: { ...headers, Authorization: '' } };
      }
    }
    const authData = getAuth();
    const authorization = (options as { authorization: string }).authorization;
    if (!authData || authData.authorization !== authorization) {
      removeAuth();
      signOut('401');
      return { headers: { ...headers, Authorization: '' } };
    }
    return { headers: { ...headers, Authorization: authorization } };
  });

  const httpLink = createHttpLink({
    uri: getUri(),
    fetch: customFetch,
  });

  const restLink = new RestLink({
    uri: `${mapLayerApi}${getDeployedProductId()}/`,
    endpoints: {
      mapLayer: `${mapLayerApi}${getDeployedProductId()}/`,
    },
  });

  return new ApolloClient({
    cache,
    link: ApolloLink.from([authLink, restLink, httpLink]),
    defaultOptions,
    connectToDevTools: true,
  });
};
